import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import axios from 'axios';
import * as React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { LoginApi } from '../api/LoginApi';
import { UserApi } from '../api/UserApi';
import VerbaScholaAccountCard, { accountBodySx } from '../component/VerbaScholaAccountCard';
import { useSignedInAccount } from '../component/verbaScholaLanding';
import { SUCCESS_SOFT, SUCCESS_TEXT, SUPPORT_EMAIL, primaryButtonSx } from '../style/verbaScholaPublicStyles';

type VerifyState = 'checking' | 'verified' | 'failed' | 'unavailable';

// The page the registration email links to.
export default function ValidateEmail() {
    const location = useLocation();
    const code = new URLSearchParams(location.search).get('code');
    const [state, setState] = React.useState<VerifyState>(code ? 'checking' : 'failed');
    const [retryCount, setRetryCount] = React.useState(0);
    const account = useSignedInAccount();
    const [resend, setResend] = React.useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

    const sendNewLink = () => {
        setResend('sending');
        UserApi.sendValidateEmail().then(() => setResend('sent'), () => setResend('failed'));
    };

    React.useEffect(() => {
        if (!code) return;
        let active = true;
        LoginApi.validateEmail(code)
            .then(() => { if (active) setState('verified'); })
            .catch(error => {
                if (!active) return;
                const status = axios.isAxiosError(error) ? error.response?.status : undefined;
                setState(status !== undefined && status >= 400 && status < 500 ? 'failed' : 'unavailable');
            });
        return () => { active = false; };
    }, [code, retryCount]);

    const continueButton = account
        ? <Button component={RouterLink} to={account.landing} sx={{ ...primaryButtonSx, minHeight: 48 }}>Continue to VerbaSchola</Button>
        : <Button component={RouterLink} to="/login" sx={{ ...primaryButtonSx, minHeight: 48 }}>Log in</Button>;

    if (state === 'checking') {
        return (
            <VerbaScholaAccountCard title="Verifying your email">
                <Stack direction="row" spacing={1.5} alignItems="center" role="status" sx={{ mt: 2 }}>
                    <CircularProgress size={20} aria-hidden />
                    <Typography sx={accountBodySx}>One moment…</Typography>
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    if (state === 'failed') {
        return (
            <VerbaScholaAccountCard title="We couldn’t verify your email"
                intro={account
                    ? 'Verification links expire after 7 days and stop working if your email address changes. Send yourself a new one, or open the whole link from your most recent email.'
                    : 'Verification links expire after 7 days. Make sure you opened the whole link from your most recent email, or log in to send a new one. You can still use VerbaSchola while this is sorted out.'}>
                {resend === 'sent' && <Typography role="status" sx={{ ...accountBodySx, mt: 2, color: SUCCESS_TEXT }}>We sent a new verification link. Check your email.</Typography>}
                {resend === 'failed' && <Typography role="alert" sx={{ ...accountBodySx, mt: 2, color: 'error.dark' }}>We couldn’t send a new link right now. Try again in a moment.</Typography>}
                <Stack spacing={1} sx={{ mt: 3 }}>
                    {account && resend !== 'sent' && (
                        <Button onClick={sendNewLink} disabled={resend === 'sending'} sx={{ ...primaryButtonSx, minHeight: 48 }}>
                            {resend === 'sending' ? 'Sending…' : 'Send a new verification email'}
                        </Button>
                    )}
                    {account ? <Button component={RouterLink} to={account.landing} variant="text">Continue to VerbaSchola</Button> : continueButton}
                    <Button href={`mailto:${SUPPORT_EMAIL}`} variant="text">Contact support</Button>
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    if (state === 'unavailable') {
        return (
            <VerbaScholaAccountCard title="We couldn't check your email link"
                intro="The verification service is unavailable right now. Please try again in a moment.">
                <Stack spacing={1} sx={{ mt: 3 }}>
                    <Button onClick={() => { setState('checking'); setRetryCount(count => count + 1); }}
                        sx={{ ...primaryButtonSx, minHeight: 48 }}>Try again</Button>
                    {continueButton}
                </Stack>
            </VerbaScholaAccountCard>
        );
    }

    return (
        <VerbaScholaAccountCard title="Your email is verified" intro="Thanks for confirming your email address.">
            <Box aria-hidden sx={{ mt: 3, width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: SUCCESS_SOFT, color: SUCCESS_TEXT }}>
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 32 }} />
            </Box>
            <Stack sx={{ mt: 3 }}>{continueButton}</Stack>
        </VerbaScholaAccountCard>
    );
}
